import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { formatProviderName } from '../../utils/userHelpers';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Search, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AddDiagnosisDialog - Dialog for adding a diagnosis to a client
 * 
 * Data Flow:
 * 1. User searches for diagnosis codes (ICD-10)
 * 2. User selects provider(s) responsible for the diagnosis
 * 3. User fills form and submits
 * 4. POST to /api/clients/{id}/diagnoses saves to database
 * 5. onSuccess callback triggers parent to refresh client data
 * 6. Other components (billing, med referrals, notes) fetch diagnoses from API as needed
 * 
 * @param {boolean} open - Whether dialog is open
 * @param {function} onOpenChange - Callback when open state changes
 * @param {object} client - The client to add diagnosis to (needs id, full_name, assigned_providers)
 * @param {array} users - List of users to select providers from
 * @param {object} currentUser - Current logged-in user
 * @param {function} onSuccess - Callback after successful save (receives clientId and options)
 */
export const AddDiagnosisDialog = ({ 
  open, 
  onOpenChange, 
  client, 
  users = [],
  currentUser,
  onSuccess 
}) => {
  // Debug logging
  void 0 && console.log('[AddDiagnosisDialog] Render - open:', open, 'client:', client?.id, client?.full_name);
  
  // Local state for this dialog
  const [loading, setLoading] = useState(false);
  const [diagnosisCodes, setDiagnosisCodes] = useState([]);
  const [selectedProviderIds, setSelectedProviderIds] = useState([]);
  // Most-used quick-picks (top N ICD-10 codes billed by the practice
  // over the last 90 days).  Lazy-loaded once when the dialog opens
  // so we don't pay the cost on every chart click.
  const [mostUsed, setMostUsed] = useState([]);
  // Feb 2026 — combobox UX: dropdown visibility is independent of
  // `diagnosisCodes.length` because we want to (a) close it on result
  // click without clearing the results array (so the user can re-open
  // by typing) and (b) close it on outside-click without dropping the
  // typed value.
  const [showDropdown, setShowDropdown] = useState(false);
  const codeInputWrapRef = useRef(null);
  const [formData, setFormData] = useState({
    diagnosis_code: '',
    diagnosis_description: '',
    start_date: new Date().toISOString().slice(0, 10),
    is_primary: false,
    notes: '',
    diagnosis_tag: '' // Optional tag: 'differential', 'working', or empty
  });

  // Get providers assigned to this client
  const clientProviders = (users || []).filter(u => 
    (u.role === 'provider' || u.role === 'supervisor' || u.is_provider) &&
    (client?.assigned_providers?.includes(u.id) || u.id === currentUser?.id)
  );

  const resetForm = () => {
    setFormData({
      diagnosis_code: '',
      diagnosis_description: '',
      start_date: new Date().toISOString().slice(0, 10),
      is_primary: false,
      notes: '',
      diagnosis_tag: ''
    });
    setDiagnosisCodes([]);
    setSelectedProviderIds([]);
    setShowDropdown(false);
  };

  // Close the combobox dropdown on outside-click (Radix Dialog
  // suppresses native blur events for portaled children, so we wire
  // our own mousedown listener).
  useEffect(() => {
    if (!showDropdown) return undefined;
    const onMouseDown = (e) => {
      if (codeInputWrapRef.current && !codeInputWrapRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showDropdown]);

  // Lazy-load the practice's top-N most-billed dx codes when the
  // dialog opens.  Cached for the lifetime of the dialog instance —
  // closing and reopening refetches, which is fine because (a) the
  // dialog is short-lived and (b) the chips should reflect any add
  // the user just made.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${API}/api/diagnosis-codes/most-used?days=90&limit=8`);
        if (!cancelled) {
          setMostUsed(Array.isArray(resp.data) ? resp.data : []);
        }
      } catch (err) {
        // Non-fatal — the search input still works without chips.
        if (!cancelled) setMostUsed([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!client?.id) {
      toast.error("No client selected");
      return;
    }
    
    // Require at least one provider
    if (selectedProviderIds.length === 0) {
      toast.error("Please select at least one provider responsible for this diagnosis");
      return;
    }
    
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/api/clients/${client.id}/diagnoses`, {
        ...formData,
        provider_ids: selectedProviderIds
      });
      toast.success("Diagnosis added successfully");
      onOpenChange(false);
      resetForm();
      // Trigger parent to refresh client data - pass option to stay on
      // diagnoses tab; include the created diagnosis so callers (e.g.
      // ClaimEditDialog) can append the code to a claim.
      if (onSuccess) {
        onSuccess(client.id, { initialTab: 'diagnoses', diagnosis: resp.data });
      }
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error("This diagnosis is already active for this client");
      } else {
        toast.error("Failed to add diagnosis");
      }
    } finally {
      setLoading(false);
    }
  };

  // Combobox: typing in the code field both sets the form value AND
  // triggers a search.  Picking a result fills both code + description
  // and collapses the dropdown.  Manual typing still works — users
  // who know the exact code can ignore the dropdown.
  const handleCodeChange = async (rawValue) => {
    setFormData(prev => ({ ...prev, diagnosis_code: rawValue }));
    const term = rawValue.trim();
    if (term.length >= 2) {
      try {
        const response = await axios.get(
          `${API}/api/diagnosis-codes?search=${encodeURIComponent(term)}&limit=200`
        );
        setDiagnosisCodes(response.data || []);
        setShowDropdown(true);
      } catch (error) {
        console.error('Failed to search diagnosis codes');
        setDiagnosisCodes([]);
        setShowDropdown(false);
      }
    } else {
      setDiagnosisCodes([]);
      setShowDropdown(false);
    }
  };

  const handleSelectCode = (code) => {
    setFormData(prev => ({
      ...prev,
      diagnosis_code: code.code,
      diagnosis_description: code.description
    }));
    setDiagnosisCodes([]);
    setShowDropdown(false);
  };

  const clearCode = () => {
    setFormData(prev => ({
      ...prev,
      diagnosis_code: '',
      diagnosis_description: ''
    }));
    setDiagnosisCodes([]);
    setShowDropdown(false);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) {
        resetForm();
      }
      onOpenChange(isOpen);
    }} modal={true}>
      <DialogContent className="max-w-2xl !z-[9999] max-h-[90vh] overflow-y-auto" style={{ zIndex: 9999 }}>
        <DialogHeader>
          <DialogTitle>Add Diagnosis - {client?.full_name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Diagnosis Code (ICD-10) — combobox: typing searches,
              clicking a result fills both code + description.  A
              single field replaces the previously confusing
              "Search Diagnosis Codes" + "Diagnosis Code" pair. */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2" ref={codeInputWrapRef}>
              <Label htmlFor="diagnosis_code">Diagnosis Code (ICD-10) *</Label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  id="diagnosis_code"
                  value={formData.diagnosis_code}
                  onChange={(e) => handleCodeChange(e.target.value)}
                  onFocus={() => { if (diagnosisCodes.length > 0) setShowDropdown(true); }}
                  placeholder="Type a code or condition (e.g., F32, depression)…"
                  autoComplete="off"
                  required
                  className="pl-8 pr-8"
                  data-testid="diagnosis-code-input"
                />
                {formData.diagnosis_code && (
                  <button
                    type="button"
                    onClick={clearCode}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                    aria-label="Clear diagnosis code"
                    data-testid="diagnosis-code-clear"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
                {showDropdown && diagnosisCodes.length > 0 && (
                  <div
                    className="absolute left-0 right-0 top-full z-10 mt-1 max-h-60 overflow-y-auto rounded-lg border bg-white shadow-lg"
                    data-testid="diagnosis-code-dropdown"
                  >
                    <div className="sticky top-0 border-b bg-gray-50 px-2 py-1 text-xs text-gray-600">
                      {diagnosisCodes.length} match{diagnosisCodes.length === 1 ? '' : 'es'} — click to select
                    </div>
                    {diagnosisCodes.map((code) => (
                      <div
                        key={code.id}
                        className="cursor-pointer border-b p-2 hover:bg-teal-50"
                        onClick={() => handleSelectCode(code)}
                        data-testid={`diagnosis-code-option-${code.code}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <code className="rounded bg-gray-100 px-1 font-mono text-sm">{code.code}</code>
                            <span className="ml-2 text-sm">{code.description}</span>
                          </div>
                          <Badge variant="outline" className="shrink-0 text-xs">{code.code_type}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {formData.diagnosis_code && formData.diagnosis_description && !showDropdown && (
                <p className="mt-1 text-xs text-teal-700" data-testid="diagnosis-code-selected-hint">
                  ✓ {formData.diagnosis_description}
                </p>
              )}
              {/* Recently-used quick-picks — only shown when the input
                  is empty so they don't compete with active search. */}
              {!formData.diagnosis_code && mostUsed.length > 0 && (
                <div className="mt-1.5" data-testid="diagnosis-code-quick-picks">
                  <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-500">
                    Recently used by practice
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {mostUsed.map((m) => (
                      <button
                        key={m.code}
                        type="button"
                        onClick={() => handleSelectCode(m)}
                        title={`${m.description || ''} · ${m.count} claim${m.count === 1 ? '' : 's'} in last 90 days`}
                        className="flex items-center gap-1.5 rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-[11px] text-teal-800 transition-colors hover:border-teal-400 hover:bg-teal-100"
                        data-testid={`diagnosis-quick-pick-${m.code}`}
                      >
                        <code className="font-mono">{m.code}</code>
                        {m.description && (
                          <span className="max-w-[180px] truncate text-gray-700">
                            {m.description}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="start_date">Start Date *</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData(prev => ({...prev, start_date: e.target.value}))}
                required
                data-testid="diagnosis-start-date-input"
              />
            </div>
          </div>
          
          {/* Description */}
          <div>
            <Label htmlFor="diagnosis_description">Description</Label>
            <Textarea
              id="diagnosis_description"
              value={formData.diagnosis_description}
              onChange={(e) => setFormData(prev => ({...prev, diagnosis_description: e.target.value}))}
              required
              data-testid="diagnosis-description-input"
            />
          </div>
          
          {/* Provider Selection - Who is clinically responsible */}
          <div>
            <Label>Provider(s) Responsible *</Label>
            <p className="text-xs text-gray-500 mb-2">
              Select the provider(s) who are clinically responsible for this diagnosis and can bill for related services
            </p>
            <div className="border rounded-md p-3 bg-gray-50 max-h-40 overflow-y-auto">
              {clientProviders.length > 0 ? (
                <div className="space-y-2">
                  {clientProviders.map(provider => (
                    <label key={provider.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-100 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={selectedProviderIds.includes(provider.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedProviderIds(prev => [...prev, provider.id]);
                          } else {
                            setSelectedProviderIds(prev => prev.filter(id => id !== provider.id));
                          }
                        }}
                        className="rounded border-gray-300"
                        data-testid={`provider-checkbox-${provider.id}`}
                      />
                      <span className="text-sm">
                        {formatProviderName(provider)}
                        {provider.role && <span className="text-gray-500 ml-1">({provider.role})</span>}
                      </span>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No providers assigned to this client</p>
              )}
            </div>
            {selectedProviderIds.length > 0 && (
              <p className="text-xs text-teal-600 mt-1">
                {selectedProviderIds.length} provider(s) selected
              </p>
            )}
          </div>
          
          {/* Primary Checkbox */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_primary"
                checked={formData.is_primary}
                onChange={(e) => setFormData(prev => ({...prev, is_primary: e.target.checked}))}
                data-testid="diagnosis-primary-checkbox"
              />
              <Label htmlFor="is_primary">Primary Diagnosis</Label>
            </div>
          </div>
          
          {/* Diagnosis Tag (Optional) */}
          <div>
            <Label htmlFor="diagnosis_tag">Diagnosis Tag (Optional)</Label>
            <p className="text-xs text-gray-500 mb-2">
              Optionally mark this diagnosis as differential, working, rule out, by history, or provisional
            </p>
            <Select
              value={formData.diagnosis_tag}
              onValueChange={(value) => setFormData(prev => ({...prev, diagnosis_tag: value === 'none' ? '' : value}))}
            >
              <SelectTrigger className="w-[200px]" data-testid="diagnosis-tag-select">
                <SelectValue placeholder="No tag" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No tag</SelectItem>
                <SelectItem value="differential">Differential</SelectItem>
                <SelectItem value="working">Working</SelectItem>
                <SelectItem value="rule_out">Rule Out</SelectItem>
                <SelectItem value="by_history">By History</SelectItem>
                <SelectItem value="provisional">Provisional</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Notes */}
          <div>
            <Label htmlFor="diagnosis_notes">Clinical Notes</Label>
            <Textarea
              id="diagnosis_notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({...prev, notes: e.target.value}))}
              placeholder="Additional clinical notes..."
              data-testid="diagnosis-notes-input"
            />
          </div>
          
          {/* Buttons */}
          <div className="flex justify-end space-x-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              data-testid="diagnosis-cancel-btn"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-teal-600 hover:bg-teal-700"
              disabled={loading}
              data-testid="diagnosis-submit-btn"
            >
              {loading ? 'Adding...' : 'Add Diagnosis'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddDiagnosisDialog;
